/**************************************************
 * 
 * Global QMod Component Changes
 * 
 * These are temporary hotfixes until QMod makes official updates.
 * 
 **************************************************/

/**
 * Function to delay execution by a given time in milliseconds.
 */
function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Centralized function to apply all QMod accessibility fixes.
 * Called on initial load and after browser resize.
 * 
 * @since CT-784
 */
async function applyAllQmodAccessibilityFixes() {
    jq('[id^=highcharts-].highcharts-container').attr("role", "region");
    assignUniqueAriaLabels();
    assignUniqueFilterIds();
    jq('[id^=highcharts-end-of-chart-marker-]').attr("role", "region");
    
    await Promise.all([
        assignUniqueAttributes(),
        fixEmptyTableHeaders(),
        addTabIndexToVolumeToggle(),
        fixInvalidAriaAttributes(),
        assignUniqueLabels()
    ]);
    
    setAriaLabelsForIcons();
    setZeroNavChangeChevronLabels();
    setHistoricalDatePickerButtonLabels();
    fixHistoricalSortHeaders();
    fixDividendsSortHeaders();
    fixDetailedQuoteStatAccessibility();
    fixHistoricalDatePickerReflow();
    groupChartControlOptions();
    fixInvalidComboboxActiveDescendant();
}

/**
 * Debounce utility to prevent excessive function calls during resize.
 * 
 * @since CT-784
 */
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Reapply QMod accessibility fixes on browser resize.
 * Uses debounce to wait until resize is complete.
 * 
 * @since CT-784
 */
const reapplyQmodFixesOnResize = debounce(function() {
    applyAllQmodAccessibilityFixes();
}, 250);

window.addEventListener('resize', reapplyQmodFixesOnResize);

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Assigns unique aria-labels to highcharts containers.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueAriaLabels() {
    const charts = jq('[id^=highcharts-].highcharts-container');
    charts.each(function (index) {
        const uniqueLabel = `Chart Container ${index + 1}`;
        jq(this).attr("aria-label", uniqueLabel);
    });
}

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Assigns unique IDs to duplicate filters within SVG defs.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueFilterIds() {
    const filters = jq('svg defs filter[id="highcharts-drop-shadow-0"]');
    filters.each(function (index) {
        if (index > 0) {
            const newId = `highcharts-drop-shadow-${Math.random().toString(36).substr(2, 9)}`;
            jq(this).attr('id', newId);

            // Update any references to this filter id in the SVG
            const references = jq(`svg [filter="url(#highcharts-drop-shadow-0)"]`);
            references.each(function () {
                jq(this).attr('filter', `url(#${newId})`);
            });
        }
    });
}

// Call toggleTabIndex and apply all accessibility fixes once QMod content is ready
observeForContent('.highcharts-container').then(() => {
    toggleTabIndex(null, true);
    applyAllQmodAccessibilityFixes();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Fixes the unique ID and missing aria attributes.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueAttributes() {
    return new Promise((resolve) => {
        function generateUniqueId(baseId) {
            return baseId + '-' + Math.random().toString(36).substr(2, 9);
        }

        const ids = [
            'highcharts-end-of-chart-marker-0',
            'highcharts-screen-reader-region-before-0',
            'highcharts-drop-shadow-0',
            'highcharts-screen-reader-region-after-0',
            'highcharts-2ih7fo0-0',
            'highcharts-fjy84ra-0'
        ];

        ids.forEach((baseId) => {
            const elements = document.querySelectorAll(`#${baseId}`);
            elements.forEach((element, index) => {
                if (index > 0) {
                    const newId = generateUniqueId(baseId);
                    element.setAttribute('id', newId);
                }
            });
        });

        const landmarkElements = document.querySelectorAll('.highcharts-exit-anchor, .highcharts-container');
        landmarkElements.forEach((element, index) => {
            if (!element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart ${index + 1}`);
            }
        });

        const filters = document.querySelectorAll('svg defs filter[id="highcharts-drop-shadow-0"]');
        filters.forEach((filter, index) => {
            if (index > 0) {
                const newId = generateUniqueId('highcharts-drop-shadow-0');
                filter.setAttribute('id', newId);

                const references = document.querySelectorAll(`svg [filter="url(#highcharts-drop-shadow-0)"]`);
                references.forEach((reference) => {
                    jq(reference).attr('filter', `url(#${newId})`);
                });
            }
        });

        const specificElements = ['highcharts-3yiiuel-0', 'highcharts-pu44erb-0'];
        specificElements.forEach((baseId, index) => {
            const element = document.querySelector(`#${baseId}`);
            if (element && !element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart Specific ${index + 1}`);
            }
        });

        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    assignUniqueAttributes();
});

/**
 * Page Type: QMod - Historical Data
 * 
 * Creates and inserts a label for an input selected by a specific CSS selector.
 * 
 * @since QMod v1.69.0
 */
function createLabelForInputBySelector(selector, labelText) {
    let inputElement = document.querySelector(selector);
    if (!inputElement) {
        return;
    }

    let inputId = 'input-' + Math.random().toString(36).substr(2, 9);
    inputElement.id = inputId;

    let label = document.createElement('label');
    label.htmlFor = inputId;
    label.textContent = labelText;
    label.classList.add("visually-hidden");

    inputElement.parentNode.insertBefore(label, inputElement);
}

// Create and insert labels using the specific QMod selectors after delay
delay(2000).then(() => {
    createLabelForInputBySelector('#qmod-fromdate > div > div.duet-date__input-wrapper > input.duet-date__input', 'Date from');
    createLabelForInputBySelector('#qmod-todate > div > div.duet-date__input-wrapper > input.duet-date__input', 'Date to');
    setHistoricalDatePickerButtonLabels();
});

/**
 * Page Type: QMod - Historical Data
 *
 * Calendar toggle buttons need unique names (WCAG 2.4.6).
 */
function setHistoricalDatePickerButtonLabels() {
    const fromPicker = document.getElementById('qmod-fromdate');
    const toPicker = document.getElementById('qmod-todate');

    if (fromPicker) {
        const fromBtn = fromPicker.closest('.inner-addon')?.querySelector('.duet-date__toggle');
        if (fromBtn) {
            fromBtn.setAttribute('aria-label', 'Date from');
        }
    }

    if (toPicker) {
        const toBtn = toPicker.closest('.inner-addon')?.querySelector('.duet-date__toggle');
        if (toBtn) {
            toBtn.setAttribute('aria-label', 'Date to');
        }
    }
}

/**
 * Page Type: QMod - Historical Data
 *
 * Keeps the full Duet calendar (month header, nav, day names) visible at
 * reflow widths (1280px @ 400% zoom). QMOD positions dialog-content with a
 * large absolute top offset that clips the header.
 */
function isHistoricalDatePickerReflowViewport() {
    return window.innerWidth <= 767;
}

function historicalDatePickerNeedsReflowFix(dialog) {
    const content = dialog.querySelector('.duet-date__dialog-content');
    if (!content) {
        return true;
    }

    const contentStyles = getComputedStyle(content);
    if (contentStyles.position === 'absolute') {
        const topOffset = parseFloat(contentStyles.top);
        if (!Number.isNaN(topOffset) && topOffset > 100) {
            return true;
        }
    }

    return !dialog.classList.contains('qmod-date-dialog-reflow-fixed');
}

function fixHistoricalDatePickerDialogReflow(dialog) {
    if (!dialog || !dialog.classList.contains('is-active') || !isHistoricalDatePickerReflowViewport()) {
        return;
    }

    if (!historicalDatePickerNeedsReflowFix(dialog)) {
        return;
    }

    dialog.classList.add('qmod-date-dialog-reflow-fixed');

    dialog.style.setProperty('position', 'fixed', 'important');
    dialog.style.setProperty('inset', 'auto', 'important');
    dialog.style.setProperty('top', '0.75rem', 'important');
    dialog.style.setProperty('left', '50%', 'important');
    dialog.style.setProperty('right', 'auto', 'important');
    dialog.style.setProperty('bottom', 'auto', 'important');
    dialog.style.setProperty('transform', 'translateX(-50%)', 'important');
    dialog.style.setProperty('width', 'min(20rem, calc(100vw - 1.5rem))', 'important');
    dialog.style.setProperty('height', 'auto', 'important');
    dialog.style.setProperty('max-height', 'calc(100vh - 1.5rem)', 'important');
    dialog.style.setProperty('margin', '0', 'important');
    dialog.style.setProperty('overflow-x', 'hidden', 'important');
    dialog.style.setProperty('overflow-y', 'auto', 'important');
    dialog.style.setProperty('-webkit-overflow-scrolling', 'touch', 'important');
    dialog.style.setProperty('z-index', '10000', 'important');

    const content = dialog.querySelector('.duet-date__dialog-content');
    if (content) {
        content.style.setProperty('position', 'static', 'important');
        content.style.setProperty('top', 'auto', 'important');
        content.style.setProperty('left', 'auto', 'important');
        content.style.setProperty('transform', 'none', 'important');
        content.style.setProperty('width', '100%', 'important');
        content.style.setProperty('max-height', 'none', 'important');
        content.style.setProperty('padding', '0.75rem', 'important');
    }
}

function resetHistoricalDatePickerDialogReflow(dialog) {
    if (!dialog) {
        return;
    }

    dialog.classList.remove('qmod-date-dialog-reflow-fixed');
    dialog.removeAttribute('style');

    const content = dialog.querySelector('.duet-date__dialog-content');
    if (content) {
        content.removeAttribute('style');
    }
}

function fixHistoricalDatePickerReflow() {
    const activeDialogs = document.querySelectorAll(
        '#qmod-fromdate .duet-date__dialog.is-active, #qmod-todate .duet-date__dialog.is-active'
    );

    if (!isHistoricalDatePickerReflowViewport()) {
        document.querySelectorAll('#qmod-fromdate .duet-date__dialog.qmod-date-dialog-reflow-fixed, #qmod-todate .duet-date__dialog.qmod-date-dialog-reflow-fixed').forEach(resetHistoricalDatePickerDialogReflow);
        return;
    }

    document.querySelectorAll('#qmod-fromdate .duet-date__dialog:not(.is-active), #qmod-todate .duet-date__dialog:not(.is-active)').forEach(resetHistoricalDatePickerDialogReflow);

    activeDialogs.forEach(fixHistoricalDatePickerDialogReflow);
}

const debouncedFixHistoricalDatePickerReflow = debounce(fixHistoricalDatePickerReflow, 50);

function initHistoricalDatePickerReflowFix() {
    const root = document.querySelector('.historical-data');
    if (!root || root.dataset.historicalDatePickerReflowObserver === 'true') {
        return;
    }

    root.dataset.historicalDatePickerReflowObserver = 'true';

    root.addEventListener('click', function (event) {
        if (event.target.closest('.duet-date__toggle, .qmod-calendar-control .qmod-no-btn')) {
            debouncedFixHistoricalDatePickerReflow();
            delay(100).then(fixHistoricalDatePickerReflow);
        }
    });

    const observer = new MutationObserver(function (mutations) {
        const dialogClassChanged = mutations.some(function (mutation) {
            return mutation.type === 'attributes'
                && mutation.attributeName === 'class'
                && mutation.target.classList
                && mutation.target.classList.contains('duet-date__dialog');
        });

        if (dialogClassChanged) {
            debouncedFixHistoricalDatePickerReflow();
        }
    });

    observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
    });

    fixHistoricalDatePickerReflow();
}

/**
 * Page Type: QMod - Historical Data
 * 
 * Adds a span element with class "visually-hidden" to all matching th elements.
 * 
 * @since QMod v1.69.0
 */
function addSrOnlySpanToThElements() {
    // Select all th QMod elements with the specified class
    let thElements = document.querySelectorAll('th.qmod-control.control.sorting_disabled.dtr-hidden');

    // Iterate over each th element
    thElements.forEach(function(th) {
        // Check if there's already an visually-hidden span, if not, add one
        if (!th.querySelector('.visually-hidden')) {
            let span = document.createElement('span');
            span.className = 'visually-hidden';
            span.textContent = 'Control Header';
            span.setAttribute('aria-hidden', 'true');
    
            th.appendChild(span);
        }
    });
}

// Call the SR Only Function after delay
delay(2000).then(() => {
    addSrOnlySpanToThElements();
});

/**
 * Observes for content and resolves the promise once content is found.
 */
function observeForContent(selector) {
    return new Promise((resolve, reject) => {
        let checkInterval = setInterval(() => {
            if (jq(selector).html()) {
                clearInterval(checkInterval);
                resolve();
            }
        }, 100); // Check every 100 milliseconds
    });
}

/**
 * Page Type: QMod - Historical Data
 *
 * QMOD v1.94.0 adjustmentTypeOptions.change expects a native select change event
 * (f.target.value). Route custom listbox item clicks through a hidden select so
 * data reload works while keeping the accessible dropdown UI for text spacing.
 */
function getHistoricalAdjustmentTypeIndex(listboxItem) {
    const match = listboxItem.id && listboxItem.id.match(/listbox-item-(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
}

function syncHistoricalAdjustmentTypeSelect(select) {
    const modifiers = select.closest('.qmod-modifiers');
    if (!modifiers) {
        return;
    }

    const selectedItem = modifiers.querySelector('.qmod-a11y-dropdown2-listbox-item[aria-selected="true"]');
    if (!selectedItem) {
        return;
    }

    const index = getHistoricalAdjustmentTypeIndex(selectedItem);
    if (index !== null) {
        select.value = String(index);
    }
}

/**
 * QMOD comboboxes bind aria-activedescendant to currentItem. That value is -1
 * when nothing is highlighted, which produces IDs like
 * "qmod-adjustment-type-listbox-item--1" that do not exist (aria-valid-attr-value).
 * Keep the attribute only when it points at a live option.
 */
function fixInvalidComboboxActiveDescendant() {
    document.querySelectorAll('.qmod-a11y-dropdown2-combobox[aria-activedescendant]').forEach(function (combo) {
        const activeId = combo.getAttribute('aria-activedescendant');
        if (
            !activeId
            || !document.getElementById(activeId)
        ) {
            combo.removeAttribute('aria-activedescendant');
        }
    });
}

const debouncedFixInvalidComboboxActiveDescendant = debounce(fixInvalidComboboxActiveDescendant, 50);

function initComboboxActiveDescendantFix() {
    if (document.documentElement.dataset.qmodActivedescendantFix === 'true') {
        return;
    }

    document.documentElement.dataset.qmodActivedescendantFix = 'true';
    fixInvalidComboboxActiveDescendant();

    const observer = new MutationObserver(function () {
        debouncedFixInvalidComboboxActiveDescendant();
    });
    observer.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-activedescendant']
    });
}

function initHistoricalAdjustmentTypeSelectBridge() {
    const select = document.querySelector('.historical-data .qmod-pricehistory select.qmod-adjustment-type-select');
    if (!select || select.dataset.adjustmentBridge === 'true') {
        return;
    }

    select.dataset.adjustmentBridge = 'true';
    syncHistoricalAdjustmentTypeSelect(select);

    const modifiers = select.closest('.qmod-modifiers');
    if (!modifiers) {
        return;
    }

    modifiers.addEventListener('click', (event) => {
        const item = event.target.closest('.qmod-a11y-dropdown2-listbox-item');
        if (!item || !modifiers.contains(item)) {
            return;
        }

        const index = getHistoricalAdjustmentTypeIndex(item);
        if (index === null || select.value === String(index)) {
            return;
        }

        select.value = String(index);
        select.dispatchEvent(new Event('change', { bubbles: true }));
    });
}

/**
 * QMOD sortable table headers (Historical Data, Dividends, etc.)
 *
 * Inner <button> is the sort control (accessible name + keyboard).
 * <th> keeps aria-sort and stays out of the tab order (ALLY-10989).
 *
 * @since QMod v1.69.0
 */
function getQmodSortHeaderState(th) {
    if (th.classList.contains("sorting_asc")) {
        return { ariaSort: "ascending", labelSuffix: ", sorted ascending" };
    }
    if (th.classList.contains("sorting_desc")) {
        return { ariaSort: "descending", labelSuffix: ", sorted descending" };
    }
    return { ariaSort: "none", labelSuffix: ", sortable" };
}

function ensureQmodSortHeaderButton(th) {
    let btn = th.querySelector("button");
    if (btn) {
        return btn;
    }

    btn = document.createElement("button");
    btn.type = "button";

    if (th.classList.contains("qmod-textl")) {
        btn.className = "qmod-textl";
    } else {
        btn.className = "qmod-textr";
    }

    while (th.firstChild) {
        btn.appendChild(th.firstChild);
    }

    th.appendChild(btn);
    return btn;
}

function fixQmodSortHeadersInTable(table, reapplyFn) {
    if (!table) {
        return;
    }

    if (!table.querySelector("caption.visually-hidden")) {
        const caption = document.createElement("caption");
        caption.className = "visually-hidden";
        caption.textContent = "Column headers are sortable.";
        table.insertBefore(caption, table.firstChild);
    }

    table.querySelectorAll("th.sorting, th.sorting_asc, th.sorting_desc").forEach(function (th) {
        const btn = ensureQmodSortHeaderButton(th);
        const columnName = (btn.textContent || "").trim();
        const state = getQmodSortHeaderState(th);
        const sortLabel = "Sort by " + columnName + state.labelSuffix;

        th.setAttribute("aria-sort", state.ariaSort);
        th.removeAttribute("aria-label");
        th.setAttribute("tabindex", "-1");

        btn.type = "button";
        btn.setAttribute("aria-label", sortLabel);

        if (btn.dataset.qmodSortBound === "true") {
            return;
        }

        btn.dataset.qmodSortBound = "true";
        jq(btn).accessibleClick(function (evt) {
            evt.preventDefault();
            evt.stopPropagation();
            th.click();
            if (reapplyFn) {
                delay(50).then(reapplyFn);
            }
        });
    });
}

function fixDividendsSortHeaders() {
    document.querySelectorAll(".dividends-data table.qmod-table").forEach(function (table) {
        fixQmodSortHeadersInTable(table, fixDividendsSortHeaders);
    });
}

function fixHistoricalSortHeaders() {
    const wrap = document.getElementById("qmod-price-history");
    const table = wrap ? wrap.querySelector("table.qmod-table") : null;
    if (table) {
        fixQmodSortHeadersInTable(table, fixHistoricalSortHeaders);
    }
}

const debouncedFixDividendsSortHeaders = debounce(fixDividendsSortHeaders, 100);
const debouncedFixHistoricalSortHeaders = debounce(fixHistoricalSortHeaders, 100);

function initHistoricalSortHeaderFix() {
    observeForContent("#qmod-price-history th.sorting, #qmod-price-history th.sorting_asc, #qmod-price-history th.sorting_desc").then(function () {
        fixHistoricalSortHeaders();

        const wrap = document.getElementById("qmod-price-history");
        if (wrap && wrap.dataset.historicalSortObserver !== "true") {
            wrap.dataset.historicalSortObserver = "true";
            const observer = new MutationObserver(function () {
                debouncedFixHistoricalSortHeaders();
            });
            observer.observe(wrap, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ["class", "aria-label", "tabindex", "aria-sort"],
            });
        }
    });
}

observeForContent('.qmod-a11y-dropdown2-combobox').then(function () {
    initComboboxActiveDescendantFix();
});

if (jq('body').hasClass('page-type-qmod---historical-data')) {
    observeForContent('.historical-data .qmod-pricehistory select.qmod-adjustment-type-select').then(() => {
        initHistoricalAdjustmentTypeSelectBridge();
        initComboboxActiveDescendantFix();
    });

    observeForContent('#qmod-fromdate, #qmod-todate').then(() => {
        setHistoricalDatePickerButtonLabels();
        initHistoricalDatePickerReflowFix();
    });

    initHistoricalSortHeaderFix();
}

if (document.querySelector('.historical-data') && !jq('body').hasClass('page-type-qmod---historical-data')) {
    initHistoricalSortHeaderFix();
    observeForContent('#qmod-fromdate, #qmod-todate').then(function () {
        initHistoricalDatePickerReflowFix();
    });
}

if (jq("body").hasClass("page-type-qmod---dividends")) {
    observeForContent(".dividends-data th.sorting").then(function () {
        fixDividendsSortHeaders();

        const container = document.querySelector(".dividends-data");
        if (container && container.dataset.dividendsSortObserver !== "true") {
            container.dataset.dividendsSortObserver = "true";
            const observer = new MutationObserver(function () {
                debouncedFixDividendsSortHeaders();
            });
            observer.observe(container, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ["class", "aria-label", "tabindex", "aria-sort"],
            });
        }
    });
}

/**
 * Detailed quote stat modules (Quote & Chart, quote boxes)
 *
 * Associates each value with its label and groups stats under section headings
 * (WCAG 1.3.1).
 */
let detailedQuoteStatRootCounter = 0;

function fixDetailedQuoteStatAccessibility() {
    document.querySelectorAll('[data-qmod-tool="detailedquotetab"]').forEach(function (root) {
        if (!root.dataset.qmodDetailedQuoteA11yId) {
            detailedQuoteStatRootCounter += 1;
            root.dataset.qmodDetailedQuoteA11yId = "qmod-detailed-quote-" + detailedQuoteStatRootCounter;
        }
        const rootId = root.dataset.qmodDetailedQuoteA11yId;

        root.querySelectorAll(".quote-stat-module").forEach(function (module, moduleIndex) {
            const heading = module.querySelector(".header h3");
            if (heading) {
                if (!heading.id) {
                    heading.id = rootId + "-heading-" + moduleIndex;
                }
                if (!module.hasAttribute("aria-labelledby")) {
                    module.setAttribute("aria-labelledby", heading.id);
                }
                if (!module.getAttribute("role")) {
                    module.setAttribute("role", "region");
                }
            }

            module.querySelectorAll(".qmod-line").forEach(function (line, lineIndex) {
                const title = line.querySelector(":scope > .title, :scope > dt.title");
                const stat = line.querySelector(":scope > .stat, :scope > dd.stat");
                if (!title || !stat) {
                    return;
                }

                if (!title.id) {
                    const slug = (title.textContent || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "stat";
                    title.id = rootId + "-label-" + moduleIndex + "-" + lineIndex + "-" + slug;
                }

                stat.setAttribute("aria-labelledby", title.id);
            });
        });
    });
}

const debouncedFixDetailedQuoteStatAccessibility = debounce(fixDetailedQuoteStatAccessibility, 100);

function initDetailedQuoteStatAccessibility() {
    observeForContent('[data-qmod-tool="detailedquotetab"] .qmod-line').then(function () {
        fixDetailedQuoteStatAccessibility();

        document.querySelectorAll('[data-qmod-tool="detailedquotetab"]').forEach(function (root) {
            if (root.dataset.detailedQuoteStatObserver === "true") {
                return;
            }
            root.dataset.detailedQuoteStatObserver = "true";
            const observer = new MutationObserver(function () {
                debouncedFixDetailedQuoteStatAccessibility();
            });
            observer.observe(root, {
                childList: true,
                subtree: true,
                characterData: true,
            });
        });
    });
}

initDetailedQuoteStatAccessibility();

/**
 * QMod interactive chart (Quote & Chart, Chart page types)
 *
 * Groups chart dropdowns, date-range toggles, and volume control so screen
 * readers announce their relationship to the chart below (WCAG 1.3.1).
 */
function groupChartControlOptions() {
    document.querySelectorAll(".qmod-chart-display").forEach(function (display) {
        const chartBtns = display.querySelector(".qmod-chart-btns");
        if (!chartBtns) {
            return;
        }

        const ddWrap = chartBtns.querySelector(":scope > .qmod-dd-wrap");
        const buttonsRs = chartBtns.querySelector(":scope > .qmod-chart-buttons-rs");
        const mainChart = chartBtns.querySelector(":scope > .qmod-main-chart");

        if (!ddWrap || !buttonsRs || !mainChart) {
            return;
        }

        let group = chartBtns.querySelector(":scope > .qmod-chart-options-group");
        if (!group) {
            group = document.createElement("div");
            group.className = "qmod-chart-options-group";
            group.setAttribute("role", "group");
            group.setAttribute("aria-label", "Options for chart below");
            chartBtns.insertBefore(group, ddWrap);
        } else {
            group.setAttribute("role", "group");
            group.setAttribute("aria-label", "Options for chart below");
        }

        if (ddWrap.parentElement !== group) {
            group.appendChild(ddWrap);
        }
        if (buttonsRs.parentElement !== group) {
            group.appendChild(buttonsRs);
        }

        const chartRegion = mainChart.querySelector(".highcharts-container") || mainChart;
        if (!chartRegion.id) {
            chartRegion.id = "qmod-chart-region-" + Array.from(document.querySelectorAll(".qmod-chart-display")).indexOf(display);
        }

        group.querySelectorAll("button.qmod-group-btn, .qmod-volume-toggle input[type='checkbox']").forEach(function (control) {
            if (!control.getAttribute("aria-controls")) {
                control.setAttribute("aria-controls", chartRegion.id);
            }
        });
    });
}

const debouncedGroupChartControlOptions = debounce(groupChartControlOptions, 100);

observeForContent(".qmod-chart-btns .qmod-main-chart").then(function () {
    groupChartControlOptions();

    document.querySelectorAll('[data-qmod-tool="interactivechart"]').forEach(function (widget) {
        if (widget.dataset.chartOptionsGroupObserver === "true") {
            return;
        }
        widget.dataset.chartOptionsGroupObserver = "true";
        const observer = new MutationObserver(function () {
            debouncedGroupChartControlOptions();
        });
        observer.observe(widget, { childList: true, subtree: true });
    });
});

/**
 * Global for all QMod Components with Legends
 * 
 * Sets tabindex and accessible click for .qmod-legend li elements.
 */
delay(1000).then(() => {
    jq('.qmod-legend li').each(function(i) {
        jq(this).attr('tabindex', 0);
        jq(this).accessibleClick(function(evt) {
            // We want to use the accessibleClick method so that the logic surrounding
            // keys is consistent; However, avoid recursion by causing an infinite click loop
            if (evt.type != 'click') {
                jq(this).click();
                return false;
            }
        });
    });
});

/**
 * Global for all QMod Components with Main Chart
 * 
 * Fixes the unique label for qmod-main-chart.
 */
function assignUniqueLabels() {
    return new Promise((resolve) => {
        const elements = document.querySelectorAll('.qmod-main-chart');

        elements.forEach((element, index) => {
            if (!element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart ${index + 1}`);
            }
        });

        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    assignUniqueLabels();
});

/**
 * Page Type: QMod - Dividends
 * 
 * Removes invalid `aria-role` attributes. `role="link"` is redundant on `<a>`.
 * 
 * @since QMod v1.69.0
 */
function fixInvalidAriaAttributes() {
    return new Promise((resolve) => {
        const invalidAriaElements = document.querySelectorAll('[aria-role]');
        
        invalidAriaElements.forEach(element => {
            element.removeAttribute('aria-role');
        });

        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    fixInvalidAriaAttributes();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Toggles the tabindex and aria-hidden attributes of elements in the tab panels 
 * to prevent tabbing into hidden fields.
 * 
 * @since QMod v1.69.0
 */
function toggleTabIndex(event, isFirstLoad = false) {
    let next, previous;
    if (!isFirstLoad) {
        next = jq(event.target.hash);
        previous = jq(event.relatedTarget.hash);
    } else {
        next = jq('#tab_quote');
        previous = jq('#tab_historical');
    }

    // Add aria-hidden="true" and tabindex="-1" to the hidden tab-panel
    previous.attr('aria-hidden', 'true');
    previous.find('input, button:not(.no-tab), .qmod-calendar-control, .highcharts-container, .qmod-a11y-toggle-wrap, .qmod-a11y-dropdown2-combobox, .highcharts-exit-anchor').attr('tabindex', '-1');
    previous.find('th.sorting, th.sorting_asc, th.sorting_desc').attr('tabindex', '-1');
    previous.find('.highcharts-exit-anchor').attr('aria-hidden', 'true');

    // Remove aria-hidden and set tabindex="0" from the active tab-panel.
    // Sortable <th> stay at -1; the inner <button> is the tab stop (ALLY-10989).
    next.removeAttr('aria-hidden');
    next.find('input, button:not(.no-tab), .qmod-calendar-control, .highcharts-container, .qmod-a11y-toggle-wrap, .qmod-a11y-dropdown2-combobox, .highcharts-exit-anchor').attr('tabindex', '0');
    next.find('th.sorting, th.sorting_asc, th.sorting_desc').attr('tabindex', '-1');
    next.find('.highcharts-exit-anchor').attr('aria-hidden', 'false');
}

// Update table inputs on tab click
jq('.stock-tab a[data-bs-toggle="tab"]').on('shown.bs.tab', function (event) {
    toggleTabIndex(event, false);
});

// Initial load adjustments
delay(2000).then(() => {
    toggleTabIndex(null, true);
});

/**
 * Ensure dropdown in historical data pane opens on Enter key press
 */
jq(document).on('keydown', '.qmod-a11y-dropdown2', function (event) {
    if (event.key === 'Enter') {
        let expanded = jq(this).attr('aria-expanded') === 'true';
        jq(this).attr('aria-expanded', !expanded);
        jq(this).click();
    }
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Ensures that down and up arrow icons are not hidden and have the appropriate aria-labels.
 */
function setAriaLabelsForIcons() {
    const downArrowIcon = jq('.qmod-change.qmod-ch-down .qmod-change-icon.fa.fa-arrow-circle-down');
    const upArrowIcon = jq('.qmod-change.qmod-ch-up .qmod-change-icon.fa.fa-arrow-circle-up');

    if (downArrowIcon.length > 0) {
        downArrowIcon.attr('aria-hidden', 'false');
        downArrowIcon.attr('aria-label', 'Down');
        downArrowIcon.attr('role', 'img');
        downArrowIcon.removeAttr('aria-disabled');
    }

    if (upArrowIcon.length > 0) {
        upArrowIcon.attr('aria-hidden', 'false');
        upArrowIcon.attr('aria-label', 'Up');
        upArrowIcon.attr('role', 'img');
        upArrowIcon.removeAttr('aria-disabled');
    }
}

/**
 * Zero nav stock ticker — CSS chevron arrows need text alternatives (WCAG 1.1.1).
 * Applied after QMod renders and re-applied when quote data updates.
 */
function setZeroNavChangeChevronLabels() {
    document.querySelectorAll('.zero-nav-quote-box span.change').forEach((changeEl) => {
        const chevron = changeEl.querySelector('.zero-nav-change-chevron');
        if (!chevron) {
            return;
        }

        if (changeEl.classList.contains('qmod-ch-up')) {
            chevron.setAttribute('role', 'img');
            chevron.removeAttribute('aria-hidden');
            chevron.setAttribute('aria-label', 'Up arrow');
        } else if (changeEl.classList.contains('qmod-ch-down')) {
            chevron.setAttribute('role', 'img');
            chevron.removeAttribute('aria-hidden');
            chevron.setAttribute('aria-label', 'Down arrow');
        } else {
            chevron.setAttribute('aria-hidden', 'true');
            chevron.removeAttribute('aria-label');
            chevron.removeAttribute('role');
        }
    });
}

function initZeroNavChevronAccessibility() {
    setZeroNavChangeChevronLabels();

    document.querySelectorAll('.zero-nav-stock, .zero-nav-stock-mobile').forEach((container) => {
        if (container.dataset.zeroNavChevronObserved === 'true') {
            return;
        }

        container.dataset.zeroNavChevronObserved = 'true';

        const observer = new MutationObserver(() => {
            setZeroNavChangeChevronLabels();
        });

        observer.observe(container, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class'],
        });
    });
}

document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
        setAriaLabelsForIcons();
    }, 2000); // 2-second delay

    if (!document.querySelector('.zero-nav-stock, .zero-nav-stock-mobile')) {
        return;
    }

    observeForContent('.zero-nav-quote-box span.change').then(() => {
        initZeroNavChevronAccessibility();
    });

    delay(2000).then(() => {
        initZeroNavChevronAccessibility();
    });
});

/**
 * Page Type: QMod - Dividends
 * 
 * Wraps the pagination in a nav element with class "pagination-wrapper" and 
 * sets appropriate aria-labels for the nav and pagination buttons.
 */
function enhancePaginationAccessibility() {
    function stripPaginateAriaRole(root = document) {
        const buttons = root.querySelectorAll
            ? root.querySelectorAll('.paginate_button[aria-role]')
            : [];

        buttons.forEach(button => {
            button.removeAttribute('aria-role');
        });

        if (
            root.nodeType === 1
            && root.classList.contains('paginate_button')
            && root.hasAttribute('aria-role')
        ) {
            root.removeAttribute('aria-role');
        }
    }

    stripPaginateAriaRole();

    document.querySelectorAll('.dataTables_paginate').forEach(paginationEl => {
        const ariaRoleObserver = new MutationObserver(() => {
            stripPaginateAriaRole(paginationEl);
        });
        ariaRoleObserver.observe(paginationEl, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['aria-role']
        });
    });

    const pagination = document.querySelector('#DataTables_Table_1_paginate');
    
    if (pagination) {
        // Track current page to detect actual page changes
        let currentPage = null;
        let isScrolling = false;
        
        // Function to get current page number
        function getCurrentPage() {
            const currentButton = pagination.querySelector('.paginate_button.current');
            return currentButton ? currentButton.textContent.trim() : null;
        }
        
        // Initialize current page
        currentPage = getCurrentPage();
        
        // Function to scroll to top of the dividends table and set focus
        function scrollToTableTop() {
            if (isScrolling) return;
            isScrolling = true;
            
            const newPage = getCurrentPage();
            
            // Only proceed if page actually changed
            if (newPage !== currentPage) {
                currentPage = newPage;
                
                const dividendsTable = document.querySelector('.dividends-data');
                if (dividendsTable) {
                    dividendsTable.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start' 
                    });
                    
                    // Set focus to the "Dividend History" title for context
                    setTimeout(() => {
                        try {
                            // Find the "Dividend History" heading
                            const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');
                            if (dividendHistoryHeading) {
                                // Make the heading focusable and set focus
                                dividendHistoryHeading.setAttribute('tabindex', '-1');
                                dividendHistoryHeading.focus();
                            }
                        } finally {
                            isScrolling = false;
                        }
                    }, 300); // Delay to ensure smooth scroll completes and table content is updated
                } else {
                    isScrolling = false;
                }
            } else {
                isScrolling = false;
            }
        }

        // Add accessible names to both dividend tables
        function addTableAccessibleName() {
            // Handle Upcoming Dividends table (DataTables_Table_0)
            const upcomingTable = document.querySelector('#DataTables_Table_0');
            const upcomingHeading = document.querySelector('.qmod-upcoming-dividends .qmod-heading');
            
            if (upcomingTable && upcomingHeading && !upcomingTable.hasAttribute('aria-labelledby')) {
                // Use aria-labelledby to reference the heading
                const headingId = 'upcoming-dividends-heading';
                upcomingHeading.setAttribute('id', headingId);
                upcomingTable.setAttribute('aria-labelledby', headingId);
            }
            
            // Handle Dividend History table (DataTables_Table_1)
            const historyTable = document.querySelector('#DataTables_Table_1');
            const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');
            
            if (historyTable && dividendHistoryHeading && !historyTable.hasAttribute('aria-labelledby')) {
                // Use aria-labelledby to reference the heading
                const headingId = 'dividend-history-heading';
                dividendHistoryHeading.setAttribute('id', headingId);
                historyTable.setAttribute('aria-labelledby', headingId);
            }
        }

        // Create a nav element with the class "pagination-wrapper" and aria-label "Pagination"
        const navWrapper = document.createElement('nav');
        navWrapper.classList.add('pagination-wrapper');
        navWrapper.setAttribute('aria-label', 'Pagination');

        // Wrap the pagination element with the nav element
        pagination.parentNode.insertBefore(navWrapper, pagination);
        navWrapper.appendChild(pagination);

        // Function to set aria-labels for the next and previous buttons
        function setAriaLabels() {
            const prevButton = pagination.querySelector('.previous');
            const nextButton = pagination.querySelector('.next');
            
            if (prevButton) {
                prevButton.setAttribute('aria-label', 'Previous Page');
                // Fixes aria-prohibited-attr violation
                prevButton.setAttribute('role', 'link');
            }
            
            if (nextButton) {
                nextButton.setAttribute('aria-label', 'Next Page');
                // Fixes aria-prohibited-attr violation
                nextButton.setAttribute('role', 'link');
            }

            // Set aria-current="page" for the active page
            const pageButtons = pagination.querySelectorAll('.paginate_button');
            pageButtons.forEach(button => {
                if (button.classList.contains('current')) {
                    button.setAttribute('aria-current', 'page');
                } else {
                    button.removeAttribute('aria-current');
                }
            
                if (!button.classList.contains('previous') && !button.classList.contains('next')) {
                    const blabel = parseInt(button.getAttribute('data-dt-idx'), 10) + 1;
                    button.setAttribute('aria-label', 'Page ' + blabel);
                }

                button.removeAttribute('aria-role');
                
            });
            
            // Add screen reader text to ellipses instead of prohibited aria-label
            const ellipses = pagination.querySelectorAll('.ellipsis');
            ellipses.forEach(ellipsis => {
                // Check if visually-hidden span already exists to avoid duplicates
                if (!ellipsis.querySelector('.visually-hidden')) {
                    const srOnlySpan = document.createElement('span');
                    srOnlySpan.className = 'visually-hidden';
                    srOnlySpan.textContent = 'Ellipsis indicating non-visible pages';
                    ellipsis.appendChild(srOnlySpan);
                }
            });
        };

        // Add accessible names to both dividend tables
        function addTableAccessibleName() {
            // Handle Upcoming Dividends table (DataTables_Table_0)
            const upcomingTable = document.querySelector('#DataTables_Table_0');
            const upcomingHeading = document.querySelector('.qmod-upcoming-dividends .qmod-heading');
            
            if (upcomingTable && upcomingHeading && !upcomingTable.hasAttribute('aria-labelledby')) {
                // Use aria-labelledby to reference the heading
                const headingId = 'upcoming-dividends-heading';
                upcomingHeading.setAttribute('id', headingId);
                upcomingTable.setAttribute('aria-labelledby', headingId);
            }
            
            // Handle Dividend History table (DataTables_Table_1)
            const historyTable = document.querySelector('#DataTables_Table_1');
            const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');
            
            if (historyTable && dividendHistoryHeading && !historyTable.hasAttribute('aria-labelledby')) {
                // Use aria-labelledby to reference the heading
                const headingId = 'dividend-history-heading';
                dividendHistoryHeading.setAttribute('id', headingId);
                historyTable.setAttribute('aria-labelledby', headingId);
            }
        }

        // Apply aria-labels initially
        setAriaLabels();
        
        // Add table accessibility initially
        addTableAccessibleName();

        // Simple MutationObserver for aria labels and table accessibility only
        const observer = new MutationObserver(() => {
            setAriaLabels();
            addTableAccessibleName(); // Ensure table accessibility is maintained
        });
        observer.observe(pagination, { childList: true, subtree: true });

        // Use event delegation to handle pagination clicks and keyboard events
        pagination.addEventListener('click', (e) => {
            const button = e.target.closest('.paginate_button:not(.disabled)');
            if (button && !button.classList.contains('current')) {
                setTimeout(scrollToTableTop, 200);
            }
        });

        // Also listen for keyboard events (Enter/Space)
        pagination.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                const button = e.target.closest('.paginate_button:not(.disabled)');
                if (button && !button.classList.contains('current')) {
                    setTimeout(scrollToTableTop, 200);
                }
            }
        });

    }
}

// Call the function with a delay after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    delay(2000).then(() => {
        fixInvalidAriaAttributes();
        enhancePaginationAccessibility();
    });
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Adds meaningful aria-label to empty table headers.
 * 
 * @since QMod v1.69.0
 */
function fixEmptyTableHeaders() {
    return new Promise((resolve) => {
        const emptyHeaders = document.querySelectorAll('th.qmod-control.control.sorting_disabled[aria-label=""]');
        emptyHeaders.forEach(header => {
            header.setAttribute('aria-label', 'Empty header');
        });
        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    fixEmptyTableHeaders();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Adds a valid role to .qmod-a11y-toggle-wrap.qmod-volume-toggle elements.
 * 
 * @since QMod v1.69.0
 */
function addTabIndexToVolumeToggle() {
    return new Promise((resolve) => {
        const toggleWrapElements = document.querySelectorAll('.qmod-a11y-toggle-wrap.qmod-volume-toggle');
        toggleWrapElements.forEach(element => {
            element.setAttribute('tabindex', '-1');
        });
        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    addTabIndexToVolumeToggle();
});